// src/hooks/order/useRecogeEnBodega.ts
// Hook para manejar la lógica de "Recoge en bodega"

import { useState, useEffect, useCallback } from 'react'
import type { OrderData } from '../../types/orderFormTypes'

/** Valor interno del select; en API ecommerce se envía como cadena vacía */
export type BodegaRecogidaSeleccion = '' | '95' | 'ecommerce'

function getMetaValue(initialData: OrderData | undefined, key: string): string | undefined {
  if (!initialData?.meta_data) return undefined
  const md: unknown = initialData.meta_data
  if (Array.isArray(md)) {
    const item = (md as Array<{ key: string; value: unknown }>).find((m) => m.key === key)
    if (item == null || item.value === undefined || item.value === null) return undefined
    return String(item.value)
  }
  if (md && typeof md === 'object' && !Array.isArray(md)) {
    const v = (md as Record<string, unknown>)[key]
    if (v === undefined || v === null) return undefined
    return String(v)
  }
  return undefined
}

/** `id_market` en la raíz del JSON del pedido (p. ej. GET /list/:id) */
function getRootIdMarket(initialData: OrderData | undefined): string | undefined {
  if (!initialData) return undefined
  if (!Object.prototype.hasOwnProperty.call(initialData, 'id_market')) return undefined
  const v = (initialData as Record<string, unknown>).id_market
  if (v === undefined || v === null) return undefined
  return String(v)
}

function inferRecogeEnBodega(initialData: OrderData | undefined): boolean {
  if (!initialData) return false
  if (
    initialData.meta_data?.recoge_en_bodega === 1 ||
    initialData.extraDetails?.recoge_en_bodega === 1
  ) {
    return true
  }
  const root = getRootIdMarket(initialData)
  if (root === undefined) return false
  const s = root.trim()
  return s === '95' || s === '94'
}

function seleccionFromInitial(initialData: OrderData | undefined, mode: 'view' | 'edit' | 'create'): BodegaRecogidaSeleccion {
  if ((mode !== 'edit' && mode !== 'view') || !initialData) return ''
  if (!inferRecogeEnBodega(initialData)) return ''
  const raw =
    getRootIdMarket(initialData) ??
    getMetaValue(initialData, 'id_market') ??
    getMetaValue(initialData, 'bodega_recogida_id') ??
    ''
  const normalized = String(raw).trim()
  if (normalized === '95' || normalized === '94') return '95'
  return 'ecommerce'
}

interface UseRecogeEnBodegaProps {
  initialData?: OrderData
  mode: 'view' | 'edit' | 'create'
  setFormData: React.Dispatch<React.SetStateAction<OrderData>>
}

interface UseRecogeEnBodegaReturn {
  recogeEnBodega: boolean
  setRecogeEnBodega: (value: boolean) => void
  handleRecogeEnBodegaChange: (checked: boolean) => void
  bodegaRecogidaSeleccion: BodegaRecogidaSeleccion
  handleBodegaRecogidaSeleccionChange: (value: BodegaRecogidaSeleccion) => void
}

/** Bodega Xerox (id_market 95 / 94) */
const BODEGA_XEROX_ADDRESS = {
  address_1: 'Carrera 43A #15 sur 15, Medellin, Antioquia',
  address_2: '',
  state: '05',
  city: '05001'
}

/** Punto ecommerce (recogida distinta a Xerox) */
const BODEGA_ECOMMERCE_ADDRESS = {
  address_1: 'Calle 25 A # 43B - 199, Medellin, Antioquia',
  address_2: '',
  state: '05',
  city: '05001'
}

function patchDireccionRecogida(sel: BodegaRecogidaSeleccion) {
  if (sel === '95') return BODEGA_XEROX_ADDRESS
  if (sel === 'ecommerce') return BODEGA_ECOMMERCE_ADDRESS
  return {
    address_1: '',
    address_2: '',
    state: '',
    city: ''
  }
}

export function useRecogeEnBodega({
  initialData,
  mode,
  setFormData
}: UseRecogeEnBodegaProps): UseRecogeEnBodegaReturn {
  const [recogeEnBodega, setRecogeEnBodega] = useState<boolean>(() => {
    if ((mode === 'edit' || mode === 'view') && initialData) {
      return inferRecogeEnBodega(initialData)
    }
    return false
  })

  const [bodegaRecogidaSeleccion, setBodegaRecogidaSeleccion] = useState<BodegaRecogidaSeleccion>(() =>
    seleccionFromInitial(initialData, mode)
  )

  // Inicializar recogeEnBodega y bodega al cargar pedido (edit / view)
  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && initialData) {
      const recogeEnBodegaValue = inferRecogeEnBodega(initialData)
      setRecogeEnBodega(recogeEnBodegaValue)
      setBodegaRecogidaSeleccion(recogeEnBodegaValue ? seleccionFromInitial(initialData, mode) : '')
    }
  }, [mode, initialData])

  // Sincronizar direcciones según bodega (Xerox vs Ecommerce); vacío hasta elegir bodega
  useEffect(() => {
    if (!recogeEnBodega) return
    const patch = patchDireccionRecogida(bodegaRecogidaSeleccion)
    setFormData(prev => ({
      ...prev,
      billingAddress: {
        ...prev.billingAddress,
        address_1: patch.address_1,
        address_2: patch.address_2,
        state: patch.state,
        city: patch.city
      },
      shippingAddress: {
        ...prev.shippingAddress,
        address_1: patch.address_1,
        address_2: patch.address_2,
        state: patch.state,
        city: patch.city
      }
    }))
  }, [recogeEnBodega, bodegaRecogidaSeleccion, setFormData])

  const handleBodegaRecogidaSeleccionChange = useCallback((value: BodegaRecogidaSeleccion) => {
    setBodegaRecogidaSeleccion(value)
  }, [])

  // Handler para cambiar el estado de "Recoge en bodega"
  const handleRecogeEnBodegaChange = useCallback((checked: boolean) => {
    setRecogeEnBodega(checked)
    setBodegaRecogidaSeleccion('')
    
    if (checked) {
      setFormData(prev => ({
        ...prev,
        extraDetails: {
          ...prev.extraDetails,
          transportadora: 'Entrega PDV'
        }
      }))
    } else {
      // Limpiar direcciones cuando se desmarca
      setFormData(prev => ({
        ...prev,
        billingAddress: {
          ...prev.billingAddress,
          address_1: '',
          address_2: '',
          state: '',
          city: ''
        },
        shippingAddress: {
          ...prev.shippingAddress,
          address_1: '',
          address_2: '',
          state: '',
          city: ''
        },
        extraDetails: {
          ...prev.extraDetails,
          transportadora: ''
        }
      }))
    }
  }, [setFormData])

  return {
    recogeEnBodega,
    setRecogeEnBodega,
    handleRecogeEnBodegaChange,
    bodegaRecogidaSeleccion,
    handleBodegaRecogidaSeleccionChange
  }
}

